import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Heart, Target, Sparkles, ExternalLink } from 'lucide-react';
import { useSiteImage } from '../lib/siteAssets';
import { usePageSeo } from '../lib/seo';
const ABOUT_DEFAULT = 'https://static.prod-images.emergentagent.com/jobs/6fa42141-fdc2-4ef8-946b-3af9a9eb53ac/images/10b4a3bdbe405c716fe7bf881739dff3090bd407a10d9d8f83085a087f9a098c.png';

const AboutPage = () => {
  usePageSeo('about', { title: 'About The Expat Studio', description: 'The Expat Studio creates considered, comprehensive relocation guides for Australians starting a new life in Europe.' });
  const aboutSrc = useSiteImage('about_image', ABOUT_DEFAULT);
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const values = [
    {
      icon: Target,
      title: 'Researched & Accurate',
      description: 'Every piece of information is carefully researched and verified with official sources.'
    },
    {
      icon: Sparkles,
      title: 'Beautifully Designed',
      description: 'Because your relocation planning should be as beautiful as your destination.'
    },
    {
      icon: Heart,
      title: 'Practical & Actionable',
      description: 'Clear steps you can actually follow, not just general advice.'
    }
  ];

  const sisterBrands = [
    {
      name: 'PB Travel Angel',
      description: 'Premium travel planning resources for meaningful adventures.',
      url: 'https://pbtravelangel.com'
    },
    {
      name: 'CloudMyLuxury',
      description: 'Curated luxury lifestyle and premium product reviews.',
      url: 'https://cloudmyluxury.com'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section data-testid="about-hero" className="container-custom mb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={aboutSrc}
              alt="Kellie, Founder of The Expat Studio"
              className="w-full max-w-md mx-auto rounded-3xl shadow-2xl"
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-[#C5A059] text-sm font-medium tracking-wider uppercase mb-4">
              Our Story
            </span>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-[#1E1622] leading-tight mb-6">
              Built by an expat, for expats.
            </h1>
            <div className="space-y-4 text-[#1E1622]/70 leading-relaxed">
              <p>
                I'm Kellie, an Australian planning my own relocation to Italy. When I started 
                researching the move, I was overwhelmed — scattered government websites, 
                outdated blog posts, conflicting advice.
              </p>
              <p>
                So I created the guides I wished existed.
              </p>
              <p>
                Every product in The Expat Studio is researched, organised, and beautifully 
                designed to make your international move feel manageable. Because it should be 
                exciting, not terrifying.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="about-values" className="bg-[#F5EFE9] section-padding">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#1E1622] mb-4">
              Our Values
            </h2>
            <p className="text-[#1E1622]/70 max-w-2xl mx-auto">
              What makes The Expat Studio different
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-hover p-8 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#3B164C]/10 flex items-center justify-center">
                  <value.icon size={28} className="text-[#3B164C]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1E1622] mb-3">
                  {value.title}
                </h3>
                <p className="text-sm text-[#1E1622]/70">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section data-testid="about-mission" className="section-padding">
        <div className="container-custom">
          <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#1E1622] mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-[#1E1622]/70 leading-relaxed mb-8">
              To turn overwhelming paperwork into clear, beautiful plans. We believe that 
              relocating internationally should feel like an adventure, not a bureaucratic nightmare.
            </p>
            <div className="inline-flex items-center gap-3 bg-[#F5EFE9] rounded-full px-6 py-3">
              <span className="text-2xl">✈️</span>
              <span className="font-serif text-lg italic text-[#3B164C]">
                "Your move, beautifully planned."
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section data-testid="about-timeline" className="bg-[#1E1622] section-padding">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#FAF7F2] mb-4">
              The Journey So Far
            </h2>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            {[
              { year: '2024', title: 'The Idea', description: 'Frustrated by scattered relocation info, the concept for The Expat Studio was born.' },
              { year: '2025', title: 'Italy Kit Launch', description: 'Our first comprehensive relocation kit for Australians moving to Italy goes live.' },
              { year: 'Coming', title: 'More Countries', description: 'Portugal, Spain, Greece, and France guides in development.' }
            ].map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 mb-8 last:mb-0"
              >
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="font-serif text-lg font-semibold text-[#3B164C]">
                    {item.year}
                  </span>
                </div>
                <div className="relative pl-6 border-l-2 border-[#FAF7F2]/20 pb-8 last:pb-0">
                  <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-[#3B164C]" />
                  <h3 className="font-serif text-xl font-semibold text-[#FAF7F2] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#FAF7F2]/70">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sister Brands */}
      <section data-testid="about-sister-brands" className="section-padding">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-12">
            <span className="inline-block text-[#C5A059] text-sm font-medium tracking-wider uppercase mb-4">
              Part of the Family
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-[#1E1622] mb-4">
              Sister Brands
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {sisterBrands.map((brand, index) => (
              <motion.a
                key={brand.name}
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`sister-brand-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="card-hover p-6 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-serif text-xl font-semibold text-[#1E1622] group-hover:text-[#3B164C] transition-colors">
                    {brand.name}
                  </h3>
                  <ExternalLink size={18} className="text-[#1E1622]/30 group-hover:text-[#3B164C] transition-colors" />
                </div>
                <p className="text-sm text-[#1E1622]/70">
                  {brand.description}
                </p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="about-cta" className="container-custom">
        <motion.div 
          {...fadeUp}
          className="bg-[#3B164C] rounded-3xl p-8 md:p-12 text-center"
        >
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-white mb-4">
            Ready to start planning your move?
          </h2>
          <p className="text-white/90 max-w-xl mx-auto mb-6">
            Browse our relocation kits or get a free guide to get started.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/shop" 
              data-testid="about-cta-shop"
              className="inline-flex items-center gap-2 bg-white text-[#3B164C] rounded-full px-8 py-3 font-medium transition-all duration-300 hover:bg-[#FAF7F2] hover:shadow-lg"
            >
              Browse Shop
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/freebie" 
              data-testid="about-cta-freebie"
              className="inline-flex items-center gap-2 text-white border border-white/30 rounded-full px-8 py-3 font-medium transition-all duration-300 hover:bg-white/10"
            >
              Free Guide
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutPage;
